/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Content, FunctionCall, GenerationConfig, Part, Tool } from "@google/generative-ai";

// Re-export from the correct Gemini API types
export type {
  Content,
  Part,
  GenerationConfig,
  Tool,
  FunctionDeclaration,
  FunctionCall,
} from "@google/generative-ai";

export type GenerativeContentBlob = {
  mimeType: string;
  data: string; // base64
};

export type Tools = Array<Tool | { googleSearch: Record<string, unknown> } | { codeExecution: Record<string, unknown> }>;

export type LiveConfig = {
  model: string;
  systemInstruction?: { parts: Part[] };
  generationConfig?: Partial<LiveGenerationConfig>;
  tools?: Tools
};

export type LiveGenerationConfig = GenerationConfig & {
  responseModalities: "text" | "audio" | "image";
  speechConfig?: {
    voiceConfig?: {
      prebuiltVoiceConfig?: {
        voiceName: "Puck" | "Charon" | "Kore" | "Fenrir" | "Aoede" | string;
      };
    };
  };
};

export type LiveOutgoingMessage =
  | SetupMessage
  | ClientContentMessage
  | RealtimeInputMessage
  | ToolResponseMessage;

export type SetupMessage = {
  setup: LiveConfig;
};

export type ClientContentMessage = {
  clientContent: {
    turns: Content[];
    turnComplete: boolean;
  };
};

export type RealtimeInputMessage = {
  realtimeInput: {
    mediaChunks: GenerativeContentBlob[];
  };
};

export type ToolResponseMessage = {
  toolResponse: {
    functionResponses: LiveFunctionResponse[];
  };
};

export type ToolResponse = ToolResponseMessage["toolResponse"];

export type LiveFunctionResponse = {
  response: Record<string, unknown>;
  id: string;
};

/** Incoming types */

export type LiveIncomingMessage =
  | ToolCallCancellationMessage
  | ToolCallMessage
  | ServerContentMessage
  | SetupCompleteMessage;

export type SetupCompleteMessage = { setupComplete: Record<string, unknown> };

export type ServerContentMessage = {
  serverContent: ServerContent;
};

export type ServerContent = ModelTurn | TurnComplete | Interrupted;

export type ModelTurn = {
  modelTurn: {
    parts: Part[];
  };
};

export type TurnComplete = { turnComplete: boolean };

export type Interrupted = { interrupted: true };

export type ToolCallCancellationMessage = {
  toolCallCancellation: {
    ids: string[];
  };
};

export type ToolCallCancellation =
  ToolCallCancellationMessage["toolCallCancellation"];

export type ToolCallMessage = {
  toolCall: ToolCall;
};

export type LiveFunctionCall = FunctionCall & {
  id: string;
};

/**
 * A `toolCall` message
 */
export type ToolCall = {
  functionCalls: LiveFunctionCall[];
};

/** log types */
export type StreamingLog = {
  date: Date;
  type: string;
  count?: number;
  message: LiveIncomingMessage | LiveOutgoingMessage | string;
};

// Type guards
export function isSetupCompleteMessage(
  message: LiveIncomingMessage,
): message is SetupCompleteMessage {
  return "setupComplete" in message;
}

export function isToolCallMessage(
  message: LiveIncomingMessage | LiveOutgoingMessage,
): message is ToolCallMessage {
  return "toolCall" in message;
}

export function isToolCallCancellationMessage(
  message: LiveIncomingMessage | LiveOutgoingMessage,
): message is ToolCallCancellationMessage {
  return "toolCallCancellation" in message;
}

export function isToolResponseMessage(
  message: LiveIncomingMessage | LiveOutgoingMessage,
): message is ToolResponseMessage {
  return "toolResponse" in message;
}

export function isServerContentMessage(
  message: LiveIncomingMessage | LiveOutgoingMessage,
): message is ServerContentMessage {
  return "serverContent" in message;
}

export function isClientContentMessage(
  message: LiveIncomingMessage | LiveOutgoingMessage,
): message is ClientContentMessage {
  return "clientContent" in message;
}

export function isModelTurn(serverContent: ServerContent): serverContent is ModelTurn {
  return "modelTurn" in serverContent;
}

export function isTurnComplete(serverContent: ServerContent): serverContent is TurnComplete {
  return "turnComplete" in serverContent;
}

export function isInterrupted(serverContent: ServerContent): serverContent is Interrupted {
  return "interrupted" in serverContent;
}

// Utility function with proper typing
export function tryParseCodeExecutionResult(output: string): string {
  try {
    const parsed = JSON.parse(output);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return output;
  }
}